import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import dayjs from 'dayjs';

import { BreadcrumbHeader, EmptyState } from '../../components/shared';
import { supplierPayments, purchaseOrders } from '../../services/dataClient';
import '../../styles/design-tokens.css';
import { fieldSx } from '../../theme/fieldStyles';

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n ?? 0);

const fmtDate = (d) => {
  if (!d) return '—';
  const [y, m, day] = d.split('-');
  return new Date(+y, +m - 1, +day).toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  });
};

const TODAY = dayjs().format('YYYY-MM-DD');

const effectiveStatus = (p) =>
  p.status === 'Pending' && p.dueDate < TODAY ? 'Overdue' : p.status;

// ─── Table style constants ────────────────────────────────────────────────────

// ─── Status badge ─────────────────────────────────────────────────────────────
function PaymentStatusBadge({ status }) {
  const styles = {
    Paid:    { bg: '#E8F5E9', color: '#2E7D32' },
    Pending: { bg: '#FFF8E1', color: '#E65100' },
    Overdue: { bg: '#FFEBEE', color: '#B71C1C' },
  };
  const s = styles[status] ?? styles.Pending;
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '10px', py: '3px', borderRadius: '10px', backgroundColor: s.bg }}>
      <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, color: s.color, lineHeight: 1 }}>
        {status}
      </Typography>
    </Box>
  );
}

// ─── Info card ────────────────────────────────────────────────────────────────
function InfoCard({ title, children }) {
  return (
    <Box sx={{ backgroundColor: '#ffffff', border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid var(--grey-200)' }}>
        <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-900)' }}>
          {title}
        </Typography>
      </Box>
      <Box sx={{ px: 2.5, py: 2 }}>{children}</Box>
    </Box>
  );
}

// ─── Info row ─────────────────────────────────────────────────────────────────
function InfoRow({ label, value }) {
  return (
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.25 }}>
      <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', flexShrink: 0, mr: 2 }}>
        {label}
      </Typography>
      <Box sx={{ textAlign: 'right' }}>
        {typeof value === 'string' || typeof value === 'number' ? (
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)', fontWeight: 500 }}>
            {value || '—'}
          </Typography>
        ) : (
          value ?? <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>—</Typography>
        )}
      </Box>
    </Box>
  );
}

// ─── Mark Paid modal ──────────────────────────────────────────────────────────
function MarkPaidModal({ open, payment, onClose, onConfirm }) {
  const [paidDate, setPaidDate] = useState(TODAY);
  const handleEnter = () => setPaidDate(TODAY);
  return (
    <Dialog
      open={open}
      onClose={(_, reason) => { if (reason !== 'backdropClick') onClose(); }}
      maxWidth="xs"
      fullWidth
      TransitionProps={{ onEnter: handleEnter }}
    >
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Mark as Paid
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: '12px !important' }}>
        {payment && (
          <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', mb: 2 }}>
            {payment.paymentNumber} — {fmt(payment.amount)} to {payment.vendorName}
          </Typography>
        )}
        <TextField
          size="small"
          label="Paid Date"
          type="date"
          value={paidDate}
          onChange={(e) => setPaidDate(e.target.value)}
          fullWidth
          InputLabelProps={{ shrink: true }}
          sx={fieldSx}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}
        >
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!paidDate}
          onClick={() => onConfirm(paidDate)}
          sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2 }}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Timeline ─────────────────────────────────────────────────────────────────
function Timeline({ payment, status }) {
  const isPaid = payment.status === 'Paid';
  const isOverdue = status === 'Overdue';

  const events = [
    {
      label: 'Payment recorded',
      date: payment.dueDate, // no createdAt; use dueDate as proxy context
      done: true,
      description: `${payment.vendorName} · ${fmt(payment.amount)}`,
    },
    {
      label: isPaid ? 'Payment settled' : isOverdue ? 'Payment due (overdue)' : 'Payment due',
      date: isPaid ? payment.paidDate : payment.dueDate,
      done: isPaid,
      overdue: isOverdue,
      description: isPaid ? `Paid on ${fmtDate(payment.paidDate)}` : `Due ${fmtDate(payment.dueDate)}`,
    },
  ];

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      {events.map((ev, idx) => (
        <Box key={idx} sx={{ display: 'flex', gap: 1.5, pb: idx < events.length - 1 ? 0 : 0 }}>
          {/* Icon + connector */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
            {ev.done ? (
              <CheckCircleOutlineIcon sx={{ fontSize: 18, color: '#009688', mt: '2px' }} />
            ) : ev.overdue ? (
              <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#B71C1C', mt: '2px' }} />
            ) : (
              <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: 'var(--grey-300)', mt: '2px' }} />
            )}
            {idx < events.length - 1 && (
              <Box sx={{ width: '2px', flex: 1, backgroundColor: 'var(--grey-200)', my: '4px' }} />
            )}
          </Box>
          {/* Text */}
          <Box sx={{ pb: idx < events.length - 1 ? 2.5 : 0 }}>
            <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: ev.overdue ? '#B71C1C' : 'var(--grey-800)' }}>
              {ev.label}
            </Typography>
            <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mt: '2px' }}>
              {ev.description}
            </Typography>
          </Box>
        </Box>
      ))}
    </Box>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────
export default function PaymentDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();

  // Local state so "Mark as Paid" updates the view without a real API
  const [payment, setPayment] = useState(() =>
    supplierPayments.find((p) => p.id === id) ?? null,
  );
  const [markPaidOpen, setMarkPaidOpen] = useState(false);

  if (!payment) {
    return (
      <Box sx={{ p: 4 }}>
        <EmptyState
          title="Payment not found"
          description="This payment may have been deleted or the link is incorrect."
        />
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
          <Typography
            onClick={() => navigate('/accounting/payments')}
            sx={{ fontSize: '0.875rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
          >
            ← Back to Payments
          </Typography>
        </Box>
      </Box>
    );
  }

  const status = effectiveStatus(payment);
  const isPending = status === 'Pending' || status === 'Overdue';

  // Linked PO lookup
  const linkedPo = payment.poId
    ? purchaseOrders.find((p) => p.id === payment.poId)
    : null;

  const handleMarkPaid = (paidDate) => {
    setPayment((prev) => ({ ...prev, status: 'Paid', paidDate }));
    setMarkPaidOpen(false);
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2 }}>
        <BreadcrumbHeader
          title={payment.paymentNumber}
          subtitle={`${payment.vendorName} · Due ${fmtDate(payment.dueDate)}`}
          crumbs={[
            { label: 'Accounting' },
            { label: 'Payments', path: '/accounting/payments' },
            { label: payment.paymentNumber },
          ]}
          actions={
            isPending && (
              <Button
                variant="contained"
                size="small"
                onClick={() => setMarkPaidOpen(true)}
                sx={{ backgroundColor: '#009688', '&:hover': { backgroundColor: '#00796B' }, textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none' }}
              >
                Mark as Paid
              </Button>
            )
          }
        />
      </Box>

      {/* Body */}
      <Box sx={{ px: 3, pb: 4, display: 'flex', gap: 3, alignItems: 'flex-start' }}>

        {/* ── Left column ── */}
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Payment summary card */}
          <Box sx={{ backgroundColor: '#ffffff', border: '1px solid var(--grey-200)', borderRadius: '8px', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid var(--grey-200)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--grey-900)' }}>
                Payment Details
              </Typography>
              <PaymentStatusBadge status={status} />
            </Box>
            <Box sx={{ px: 2.5, py: 2.5, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 48px' }}>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mb: '4px' }}>Payment Number</Typography>
                <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'var(--grey-900)' }}>{payment.paymentNumber}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mb: '4px' }}>Amount</Typography>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 700, color: 'var(--grey-900)' }}>{fmt(payment.amount)}</Typography>
              </Box>
              <Divider sx={{ gridColumn: '1 / -1', my: 2 }} />
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mb: '4px' }}>Supplier</Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: 'var(--grey-800)' }}>{payment.vendorName}</Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mb: '4px' }}>Status</Typography>
                <PaymentStatusBadge status={status} />
              </Box>
              <Divider sx={{ gridColumn: '1 / -1', my: 2 }} />
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mb: '4px' }}>Due Date</Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: status === 'Overdue' ? '#B71C1C' : 'var(--grey-800)' }}>
                  {fmtDate(payment.dueDate)}
                </Typography>
              </Box>
              <Box>
                <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)', mb: '4px' }}>Paid Date</Typography>
                <Typography sx={{ fontSize: '0.875rem', fontWeight: 500, color: payment.paidDate ? '#2E7D32' : 'var(--grey-400)' }}>
                  {payment.paidDate ? fmtDate(payment.paidDate) : '—'}
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Notes */}
          {payment.notes && (
            <InfoCard title="Notes">
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-700)', lineHeight: 1.6 }}>
                {payment.notes}
              </Typography>
            </InfoCard>
          )}

          {/* Timeline */}
          <InfoCard title="Activity">
            <Timeline payment={payment} status={status} />
          </InfoCard>

        </Box>

        {/* ── Right column ── */}
        <Box sx={{ width: 320, flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* Quick stats */}
          <InfoCard title="Summary">
            <InfoRow label="Payment #"  value={payment.paymentNumber} />
            <InfoRow label="Supplier"   value={payment.vendorName} />
            <InfoRow label="Amount"     value={fmt(payment.amount)} />
            <InfoRow label="Status"     value={<PaymentStatusBadge status={status} />} />
            <InfoRow label="Due Date"   value={fmtDate(payment.dueDate)} />
            {payment.paidDate && (
              <InfoRow
                label="Paid Date"
                value={
                  <Typography sx={{ fontSize: '0.875rem', color: '#2E7D32', fontWeight: 500 }}>
                    {fmtDate(payment.paidDate)}
                  </Typography>
                }
              />
            )}
          </InfoCard>

          {/* Linked PO */}
          <InfoCard title="Linked Purchase Order">
            {linkedPo ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>PO Number</Typography>
                  <Typography
                    onClick={() => navigate(`/accounting/purchase-orders/${linkedPo.id}`)}
                    sx={{ fontSize: '0.875rem', color: '#009688', fontWeight: 500, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    {linkedPo.poNumber}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Supplier</Typography>
                  <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{linkedPo.vendorName}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>PO Status</Typography>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', px: '8px', py: '2px', borderRadius: '10px',
                    backgroundColor: linkedPo.status === 'Received' ? '#E8F5E9' : linkedPo.status === 'Submitted' ? '#E3F2FD' : '#F5F5F5',
                  }}>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 600, lineHeight: 1,
                      color: linkedPo.status === 'Received' ? '#2E7D32' : linkedPo.status === 'Submitted' ? '#1565C0' : 'var(--grey-600)',
                    }}>
                      {linkedPo.status}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>PO Total</Typography>
                  <Typography sx={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--grey-900)' }}>{fmt(linkedPo.totalCost)}</Typography>
                </Box>
                {linkedPo.date && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: '0.75rem', color: 'var(--grey-500)' }}>Date Issued</Typography>
                    <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-800)' }}>{fmtDate(linkedPo.date)}</Typography>
                  </Box>
                )}
                <Box sx={{ mt: 0.5 }}>
                  <Typography
                    onClick={() => navigate(`/accounting/purchase-orders/${linkedPo.id}`)}
                    sx={{ fontSize: '0.8125rem', color: '#009688', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                  >
                    View Purchase Order →
                  </Typography>
                </Box>
              </Box>
            ) : (
              <Typography sx={{ fontSize: '0.875rem', color: 'var(--grey-400)' }}>
                No linked purchase order
              </Typography>
            )}
          </InfoCard>

        </Box>
      </Box>

      <MarkPaidModal
        open={markPaidOpen}
        payment={payment}
        onClose={() => setMarkPaidOpen(false)}
        onConfirm={handleMarkPaid}
      />
    </Box>
  );
}
